import { useEffect, useState } from 'react';

import { Keyboard } from 'react-native';

const KEYBOARD_HEIGHT_THRESHOLD = 100;

const NON_KEYBOARD_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

const isKeyboardTriggeringElementFocused = (): boolean => {
  const el = document.activeElement;
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) return !NON_KEYBOARD_INPUT_TYPES.has(el.type);
  return (el as HTMLElement).isContentEditable === true;
};

/**
 * Hook that returns whether the keyboard is currently visible.
 *
 * On web, viewport height can shrink for many reasons (address bar, toolbars,
 * DevTools, display scaling, macOS menu bar + dock, etc.). A virtual keyboard
 * is the only cause that ALSO has a keyboard-triggering element focused, so we
 * gate on both conditions to avoid false positives.
 */
export const useIsKeyboardVisible = () => {
  const [isKeyboardVisible, setKeyboardVisible] = useState(false);

  // Effect for iOS/Android keyboard events. Android doesn't have
  // willShow/willHide events so we use didShow/didHide instead.
  useEffect(() => {
    const didShow = Keyboard.addListener('keyboardDidShow', () => setKeyboardVisible(true));
    const didHide = Keyboard.addListener('keyboardDidHide', () => setKeyboardVisible(false));
    return () => {
      didShow.remove();
      didHide.remove();
    };
  }, []);

  // Effect for web visualViewport resize events. Tracks the viewport
  // height while no keyboard-triggering element is focused (the
  // "baseline"), then detects a keyboard when the viewport shrinks
  // significantly below that baseline while an element IS focused.
  // This works on both iOS (only visualViewport shrinks) and Android
  // Chrome (both layout and visual viewports shrink).
  useEffect(() => {
    if (typeof window !== 'undefined' && window.visualViewport) {
      // Use the larger of clientHeight and visualViewport height so that
      // the baseline is correct even if the component mounts while the
      // keyboard is already open. On iOS clientHeight stays at the full
      // pre-keyboard height; on Android both shrink, so the max is at
      // worst the same as visualViewport.height alone.
      let baselineHeight = Math.max(
        document.documentElement.clientHeight,
        window.visualViewport.height,
      );

      const resizeHandler = () => {
        if (window.visualViewport) {
          const currentHeight = window.visualViewport.height;
          const isFocused = isKeyboardTriggeringElementFocused();

          if (!isFocused) {
            baselineHeight = Math.max(document.documentElement.clientHeight, currentHeight);
          } else {
            // On iOS, clientHeight stays at the full pre-keyboard height
            // even across rotations, so keep the baseline up-to-date with
            // it while focused. On Android clientHeight shrinks with the
            // keyboard, so this is effectively a no-op.
            baselineHeight = Math.max(baselineHeight, document.documentElement.clientHeight);
          }

          const heightDiff = baselineHeight - currentHeight;
          setKeyboardVisible(heightDiff > KEYBOARD_HEIGHT_THRESHOLD && isFocused);
        }
      };

      window.visualViewport.addEventListener('resize', resizeHandler);

      return () => window.visualViewport?.removeEventListener('resize', resizeHandler);
    }
    return undefined;
  }, []);

  return isKeyboardVisible;
};
